import React, { useState } from "react";
import { motion } from "framer-motion";
import { useCart } from "../../Context/CartContext";
import { useAuth } from "../../Context/AuthContext";
import removeIcon from "../../assets/exit.png";
import AlertOverlay from "./AlertOverlay";

const CheckoutOverlay = ({ stat, toggleOverlay }) => {
  const { checkout, getTotal } = useCart();
  const { currentUser } = useAuth() || {};
  const [loading, setLoading] = useState(false);
  const [alertState, setAlertState] = useState({
    open: false,
    title: "",
    message: "",
  });
  const [clientInfo, setClientInfo] = useState({
    name: currentUser?.name || "",
    phone: currentUser?.phone || "",
    wilaya: "",
    address: "",
    email: currentUser?.email || "",
  });

  // Update clientInfo when currentUser changes
  React.useEffect(() => {
    if (currentUser) {
      setClientInfo(prev => ({
        ...prev,
        name: currentUser.name || prev.name,
        email: currentUser.email || prev.email,
        phone: currentUser.phone || prev.phone,
      }));
    }
  }, [currentUser]);

  const algeriaWilayas = [
    "Adrar", "Chlef", "Laghouat", "Oum El Bouaghi", "Batna", "Béjaïa",
    "Biskra", "Béchar", "Blida", "Bouira", "Tamanrasset", "Tébessa",
    "Tlemcen", "Tiaret", "Tizi Ouzou", "Alger", "Djelfa", "Jijel",
    "Sétif", "Saïda", "Skikda", "Sidi Bel Abbès", "Annaba", "Guelma",
    "Constantine", "Médéa", "Mostaganem", "MSila", "Mascara", "Ouargla",
    "Oran", "El Bayadh", "Illizi", "Bordj Bou Arreridj", "Boumerdès",
    "El Tarf", "Tindouf", "Tissemsilt", "El Oued", "Khenchela",
    "Souk Ahras", "Tipaza", "Mila", "Aïn Defla", "Naâma",
    "Aïn Témouchent", "Ghardaïa", "Relizane", "Timimoun",
    "Bordj Badji Mokhtar", "Ouled Djellal", "Beni Abbès", "In Salah",
    "In Guezzam", "Touggourt", "Djanet", "El M'Ghair", "El Meniaa",
  ];

  const handleCheckout = async () => {
    if (!clientInfo.name || !clientInfo.phone || !clientInfo.wilaya || !clientInfo.address) {
      setAlertState({
        open: true,
        title: "Erreur",
        message: "Veuillez remplir tous les champs requis",
      });
      return;
    }

    try {
      setLoading(true);
      const info = { ...clientInfo };
      if (currentUser) info.email = currentUser.email;
      await checkout(info);
      setLoading(false);
      toggleOverlay();
      setClientInfo({ name: "", phone: "", wilaya: "", address: "", email: "" });
      setAlertState({
        open: true,
        title: "Succès",
        message: "Commande passée avec succès",
      });
    } catch (e) {
      setLoading(false);
      setAlertState({
        open: true,
        title: "Erreur",
        message: e.message || "Impossible de passer la commande",
      });
    }
  };

  return (
    <>
    <motion.div
      className="overlay-container"
      initial={{ opacity: 0 }}
      animate={stat ? { opacity: 1 } : { opacity: 0 }}
      transition={{ duration: 0.2 }}
      style={{ display: stat ? "flex" : "none" }}
    >
      <div className="overlay-backdrop" onClick={toggleOverlay}></div>
      <motion.div
        className="overlay-content checkout-overlay-container"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={stat ? { scale: 1, opacity: 1 } : { scale: 0.8, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <div onClick={toggleOverlay} style={{ position: "absolute", right: "10px", top: "10px", cursor: "pointer" }}>
          <img src={removeIcon} alt="close" style={{ width: "20px" }} />
        </div>

        <div className="checkout-content px-20 py-20 flex-column">
          <p className="bold">Validation de Commande</p>
          <p className="small-text grey my-10">Active Fitness</p>

          <div className="checkout-total my-20">
            <p className="small-text grey">Montant total</p>
            <p className="orange bold" style={{ fontSize: "1.5rem" }}>
              {getTotal().toFixed(2)} DA
            </p>
          </div>

          <div className="checkout-form flex-column">
            <div className="input-container">
              <label className="bold small-text">Nom Complet *</label>
              <input
                className="input"
                value={clientInfo.name}
                onChange={(e) => setClientInfo({ ...clientInfo, name: e.target.value })}
                placeholder="Votre nom complet"
              />
            </div>

            <div className="input-container">
              <label className="bold small-text">Téléphone *</label>
              <input
                className="input"
                value={clientInfo.phone}
                onChange={(e) => setClientInfo({ ...clientInfo, phone: e.target.value })}
                placeholder="Votre numéro de téléphone"
              />
            </div>

            <div className="input-container">
              <label className="bold small-text">Email</label>
              <input
                className="input"
                value={clientInfo.email || (currentUser && currentUser.email) || ""}
                onChange={(e) => setClientInfo({ ...clientInfo, email: e.target.value })}
                placeholder="Votre email"
                disabled={currentUser ? true : false}
              />
            </div>

            <div className="input-container">
              <label className="bold small-text">Wilaya *</label>
              <select
                className="input"
                value={clientInfo.wilaya}
                onChange={(e) => setClientInfo({ ...clientInfo, wilaya: e.target.value })}
                required
              >
                <option value="">-- Choisir une wilaya --</option>
                {algeriaWilayas.map((w, index) => (
                  <option key={index} value={w}>
                    {w}
                  </option>
                ))}
              </select>
            </div>

            <div className="input-container">
              <label className="bold small-text">Adresse *</label>
              <input
                className="input"
                value={clientInfo.address}
                onChange={(e) => setClientInfo({ ...clientInfo, address: e.target.value })}
                placeholder="Votre adresse de livraison"
              />
            </div>
          </div>

          <div className="checkout-actions flex align-items-center justify-space-between mt-20">
            <div className="action-button" onClick={toggleOverlay}>
              <p className="btn grey small-text">Annuler</p>
            </div>

            <motion.div className="action-button bg-orange fit-content flex align-items-center justify-center" style={{ position: "relative", minWidth: "100px", height: "40px" }}>
              <motion.p
                className="btn"
                initial={{ opacity: 1 }}
                animate={!loading ? { opacity: 1 } : { opacity: 0 }}
                onClick={handleCheckout}
              >
                Confirmer
              </motion.p>

              <motion.div
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                className="flex justify-center align-items-center"
                initial={{ opacity: 0 }}
                animate={loading ? { opacity: 1 } : { opacity: 0 }}
              >
                <div style={{ width: "15px", height: "15px", border: "2px solid white", borderTop: "2px solid transparent", borderRadius: "50%", animation: "spin 0.6s linear infinite" }}></div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </motion.div>
    </motion.div>
    <AlertOverlay
      stat={alertState.open}
      title={alertState.title}
      message={alertState.message}
      onClose={() => setAlertState((prev) => ({ ...prev, open: false }))}
      autoCloseMs={2500}
    />
    </>
  );
};

export default CheckoutOverlay;
